import { useEffect } from 'react';
import api from '../services/api';

/**
 * useBadgeSync
 * - On mount: fetches /api/badges and posts SET_BADGE to the active SW.
 * - On `visibilitychange` -> visible: re-syncs the badge from /api/badges.
 * - Once: tries to register `periodicSync` with tag `refresh-badges`
 *   (best-effort; ignored on browsers without support, e.g. iOS/Firefox).
 *
 * The /api/badges endpoint is public (no token) so this works pre-login too,
 * but it's mounted top-level after auth gates for cosmetic reasons.
 */
export default function useBadgeSync() {
  useEffect(() => {
    let cancelled = false;

    const syncBadge = async () => {
      try {
        const res = await api.get('/api/badges');
        const count = Number(res?.data?.escalations) || 0;
        const sw = navigator.serviceWorker?.controller;
        if (!cancelled && sw) {
          sw.postMessage({ type: 'SET_BADGE', count });
        }
      } catch (err) {
        // Silencioso: si /api/badges falla, no hay nada que actualizar.
        // El push y el polling de BadgesContext son las fuentes principales.
      }
    };

    // Initial sync
    syncBadge();

    // Re-sync when tab becomes visible (e.g. user opens the PWA after a push
    // arrived while the app was closed).
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        syncBadge();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Best-effort: register Periodic Background Sync as a safety net.
    // Browser decides actual cadence (typically >=12h). Wrapped in try/catch.
    (async () => {
      try {
        if (!('serviceWorker' in navigator)) return;
        const registration = await navigator.serviceWorker.ready;
        if (!registration || !('periodicSync' in registration)) return;
        await registration.periodicSync.register('refresh-badges', {
          minInterval: 4 * 60 * 60 * 1000, // 4h hint; UA may pick larger
        });
      } catch (_err) {
        // NotSupportedError / NotAllowedError / quota -> ignore silently.
      }
    })();

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
}
